import { useEffect, useRef, useCallback } from 'react';
import { 
  isDirectionalEvent, 
  isEnterEvent, 
  isBackEvent
} from '../utils/deviceDetection';

interface SpatialNavOptions {
  enabled?: boolean;
  onBack?: () => boolean | void; // Return true if handled
  onEnter?: (el: HTMLElement) => void;
  rootSelector?: string;
  isTv?: boolean;
}

export function useSpatialNavigation({
  enabled = true,
  onBack,
  onEnter,
  isTv = false
}: SpatialNavOptions = {}) {
  const currentFocusedRef = useRef<HTMLElement | null>(null);
  const lastGamepadActionTimeRef = useRef<number>(0);
  const lastKeyActionTimeRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);

  // Fast helper to find all visible focusable elements in DOM
  const getFocusableElements = useCallback((): HTMLElement[] => {
    if (typeof document === 'undefined') return [];
    
    // Check if an interactive modal, drawer, or open dropdown is open
    const activeModal = document.querySelector<HTMLElement>(
      '#iptv-category-dropdown-menu, #iptv-source-dropdown-menu, #collections-dropdown-menu, #user-dropdown-menu, [role="dialog"], #video-player-modal, #episode-detail-modal, #media-detail-modal, #auth-modal, #admin-dashboard-modal, #profile-modal, #shortcuts-modal, #device-mode-modal, #iptv-video-player, #iptv-player-settings-modal, #iptv-channel-guide-drawer, .modal-dialog'
    );

    const container = activeModal || document.body;

    const selector = [
      '[data-focusable="true"]:not([tabindex="-1"])',
      'button:not([disabled]):not([tabindex="-1"])',
      'a[href]:not([tabindex="-1"])',
      'input:not([disabled]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      '[tabindex="0"]'
    ].join(', ');

    const elements = Array.from(container.querySelectorAll<HTMLElement>(selector));
    
    // Fast filtering: exclude hidden elements and sub-elements nested inside other focusable cards
    return elements.filter((el) => {
      // Must not be hidden
      if (el.offsetParent === null && el.style.position !== 'fixed') {
        return false;
      }
      if (el.getAttribute('aria-hidden') === 'true') {
        return false;
      }

      // Exclude child elements inside an outer focusable card (e.g. quick-action buttons inside MediaCard)
      const parentCard = el.parentElement?.closest('[data-focusable="true"]');
      if (parentCard && parentCard !== el) {
        return false;
      }
      
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return false;

      return true;
    });
  }, []);

  // Dedicated Dropdown Menu Handler (Collections, User Menu, IPTV Source & Category Dropdowns)
  const handleDropdownNavigation = useCallback((
    currentEl: HTMLElement,
    direction: 'up' | 'down' | 'left' | 'right'
  ): HTMLElement | null => {
    // Check open dropdown menus
    const iptvCatDropdown = document.querySelector<HTMLElement>('#iptv-category-dropdown-menu');
    const iptvSourceDropdown = document.querySelector<HTMLElement>('#iptv-source-dropdown-menu');
    const colDropdown = document.querySelector<HTMLElement>('#collections-dropdown-menu');
    const userDropdown = document.querySelector<HTMLElement>('#user-dropdown-menu');

    // Find the relevant active dropdown
    let activeDropdown: HTMLElement | null = null;
    if (iptvCatDropdown && (iptvCatDropdown.contains(currentEl) || currentEl.id === 'iptv-category-selector-btn')) {
      activeDropdown = iptvCatDropdown;
    } else if (iptvSourceDropdown && (iptvSourceDropdown.contains(currentEl) || currentEl.id === 'iptv-source-selector-btn')) {
      activeDropdown = iptvSourceDropdown;
    } else if (colDropdown && (colDropdown.contains(currentEl) || currentEl.id === 'nav-collections-dropdown')) {
      activeDropdown = colDropdown;
    } else if (userDropdown && (userDropdown.contains(currentEl) || currentEl.id === 'user-profile-button')) {
      activeDropdown = userDropdown;
    } else {
      activeDropdown = iptvCatDropdown || iptvSourceDropdown || colDropdown || userDropdown;
    }

    if (!activeDropdown) return null;

    const dropdownFocusables = Array.from(
      activeDropdown.querySelectorAll<HTMLElement>('[data-focusable="true"], button:not([disabled]), a[href], input:not([disabled])')
    ).filter(el => {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getBoundingClientRect().height > 0;
    });

    if (dropdownFocusables.length === 0) return null;

    const isInside = activeDropdown.contains(currentEl);
    let triggerId = '';
    if (activeDropdown.id === 'collections-dropdown-menu') triggerId = 'nav-collections-dropdown';
    else if (activeDropdown.id === 'user-dropdown-menu') triggerId = 'user-profile-button';
    else if (activeDropdown.id === 'iptv-source-dropdown-menu') triggerId = 'iptv-source-selector-btn';
    else if (activeDropdown.id === 'iptv-category-dropdown-menu') triggerId = 'iptv-category-selector-btn';

    const triggerBtn = triggerId ? document.getElementById(triggerId) : null;

    if (isInside) {
      const currentIndex = dropdownFocusables.indexOf(currentEl);
      if (direction === 'down') {
        if (currentIndex !== -1 && currentIndex < dropdownFocusables.length - 1) {
          const nextEl = dropdownFocusables[currentIndex + 1];
          nextEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          return nextEl;
        }
        return currentEl; // Stay inside dropdown at bottom
      }
      if (direction === 'up') {
        if (currentIndex > 0) {
          const prevEl = dropdownFocusables[currentIndex - 1];
          prevEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          return prevEl;
        }
        // At top of dropdown: go back to trigger button
        if (triggerBtn) {
          triggerBtn.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          return triggerBtn;
        }
        return currentEl;
      }
      if (direction === 'left' || direction === 'right') {
        // Exit back to trigger button
        if (triggerBtn) return triggerBtn;
        return currentEl;
      }
    } else if (currentEl.id === triggerId) {
      if (direction === 'down') {
        // Focus the currently selected item if available, or first item
        const selected = activeDropdown.querySelector<HTMLElement>('.bg-red-600, .bg-sky-600, .bg-amber-600') || dropdownFocusables[0];
        if (selected) {
          selected.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          return selected;
        }
        return dropdownFocusables[0];
      }
    }

    return null;
  }, []);

  // Dedicated Search Drawer Navigation Handler
  const handleSearchDrawerNavigation = useCallback((
    currentEl: HTMLElement,
    direction: 'up' | 'down' | 'left' | 'right'
  ): HTMLElement | null => {
    const searchDrawer = document.querySelector('#navbar-search-drawer');
    if (!searchDrawer) return null;

    const isInsideSearch = Boolean(currentEl.closest('#navbar-search-drawer') || currentEl.id === 'search-input-field');
    if (!isInsideSearch) return null;

    // 1. Search Input Field
    if (currentEl.id === 'search-input-field') {
      if (direction === 'down') {
        const filterTab = searchDrawer.querySelector<HTMLElement>('#search-filter-all, #search-filter-series');
        if (filterTab) return filterTab;
        const firstChip = searchDrawer.querySelector<HTMLElement>('#search-chip-0');
        if (firstChip) return firstChip;
        const firstResult = searchDrawer.querySelector<HTMLElement>('[id^="search-result-"]');
        if (firstResult) return firstResult;
      }
      if (direction === 'right') {
        const closeBtn = searchDrawer.querySelector<HTMLElement>('#close-search-drawer');
        if (closeBtn) return closeBtn;
      }
      if (direction === 'up') {
        const navSearch = document.querySelector<HTMLElement>('#nav-search, #nav-home');
        if (navSearch) return navSearch;
      }
      return null;
    }

    // 2. Close Search Drawer Button
    if (currentEl.id === 'close-search-drawer') {
      if (direction === 'left') {
        const searchInput = searchDrawer.querySelector<HTMLElement>('#search-input-field');
        if (searchInput) return searchInput;
      }
      if (direction === 'down') {
        const firstResult = searchDrawer.querySelector<HTMLElement>('[id^="search-result-"]');
        if (firstResult) return firstResult;
      }
      if (direction === 'up') {
        const navSearch = document.querySelector<HTMLElement>('#nav-search, #nav-home');
        if (navSearch) return navSearch;
      }
      return null;
    }

    // 3. Filter Buttons (Tots, Sèries, Pel·lícules)
    if (currentEl.id.startsWith('search-filter-')) {
      if (direction === 'up') {
        const searchInput = searchDrawer.querySelector<HTMLElement>('#search-input-field');
        if (searchInput) return searchInput;
      }
      if (direction === 'down') {
        const firstChip = searchDrawer.querySelector<HTMLElement>('#search-chip-0');
        if (firstChip) return firstChip;
        const firstResult = searchDrawer.querySelector<HTMLElement>('[id^="search-result-"]');
        if (firstResult) return firstResult;
      }
    }

    // 4. Search Suggestion Chips
    if (currentEl.id.startsWith('search-chip-')) {
      if (direction === 'up') {
        const filterTab = searchDrawer.querySelector<HTMLElement>('#search-filter-all');
        if (filterTab) return filterTab;
        const searchInput = searchDrawer.querySelector<HTMLElement>('#search-input-field');
        if (searchInput) return searchInput;
      }
      if (direction === 'down') {
        const firstResult = searchDrawer.querySelector<HTMLElement>('[id^="search-result-"]');
        if (firstResult) return firstResult;
      }
    }

    // 5. Search Result Cards
    if (currentEl.id.startsWith('search-result-')) {
      if (direction === 'up') {
        const resultsGrid = currentEl.parentElement;
        if (resultsGrid) {
          const currentRect = currentEl.getBoundingClientRect();
          const itemsAbove = Array.from(resultsGrid.querySelectorAll<HTMLElement>('[id^="search-result-"]'))
            .filter(el => {
              const r = el.getBoundingClientRect();
              return r.bottom < currentRect.top - 4 && Math.abs(r.left - currentRect.left) < 60;
            });
          
          if (itemsAbove.length > 0) {
            return itemsAbove[itemsAbove.length - 1];
          }
        }
        const firstChip = searchDrawer.querySelector<HTMLElement>('#search-chip-0');
        if (firstChip) return firstChip;
        const filterTab = searchDrawer.querySelector<HTMLElement>('#search-filter-all');
        if (filterTab) return filterTab;
        const searchInput = searchDrawer.querySelector<HTMLElement>('#search-input-field');
        if (searchInput) return searchInput;
      }
    }

    return null;
  }, []);

  // Dedicated Video Player Modal Navigation Handler
  const handleVideoPlayerNavigation = useCallback((
    currentEl: HTMLElement,
    direction: 'up' | 'down' | 'left' | 'right'
  ): HTMLElement | null => {
    const playerModal = document.querySelector<HTMLElement>('#video-player-modal');
    if (!playerModal) return null;

    // 0. Next Episode Countdown Overlay (Highest Priority Trap)
    const countdownOverlay = playerModal.querySelector<HTMLElement>('#player-countdown-overlay');
    if (countdownOverlay && countdownOverlay.getBoundingClientRect().height > 0) {
      const countdownButtons = Array.from(
        countdownOverlay.querySelectorAll<HTMLElement>('#countdown-play-now-button, #countdown-cancel-button')
      );
      const idx = countdownButtons.indexOf(currentEl);
      if (idx !== -1) {
        if (direction === 'left' && idx > 0) return countdownButtons[idx - 1];
        if (direction === 'right' && idx < countdownButtons.length - 1) return countdownButtons[idx + 1];
        return currentEl;
      }
      return countdownButtons[0] || currentEl;
    }

    // 1. Audio & Subtitles Dialog Dropdown
    const audioSubsDialog = playerModal.querySelector<HTMLElement>('#player-dialog-audio-subs');
    if (audioSubsDialog && audioSubsDialog.getBoundingClientRect().height > 0) {
      const dialogItems = Array.from(
        audioSubsDialog.querySelectorAll<HTMLElement>('#modal-close-audio-subs, [id^="modal-audio-"], [id^="modal-sub-"]')
      ).filter(el => el.getBoundingClientRect().height > 0);

      const idx = dialogItems.indexOf(currentEl);
      if (idx !== -1) {
        if (direction === 'down' && idx < dialogItems.length - 1) return dialogItems[idx + 1];
        if (direction === 'up' && idx > 0) return dialogItems[idx - 1];
        if (direction === 'left') {
          const btn = playerModal.querySelector<HTMLElement>('#player-audio-subs-button');
          if (btn) return btn;
        }
        return currentEl;
      }
      return dialogItems[1] || dialogItems[0] || currentEl;
    }

    // 2. Video Quality Dialog Dropdown
    const qualityDialog = playerModal.querySelector<HTMLElement>('#player-dialog-quality');
    if (qualityDialog && qualityDialog.getBoundingClientRect().height > 0) {
      const dialogItems = Array.from(
        qualityDialog.querySelectorAll<HTMLElement>('#modal-close-quality, [id^="modal-quality-"]')
      ).filter(el => el.getBoundingClientRect().height > 0);

      const idx = dialogItems.indexOf(currentEl);
      if (idx !== -1) {
        if (direction === 'down' && idx < dialogItems.length - 1) return dialogItems[idx + 1];
        if (direction === 'up' && idx > 0) return dialogItems[idx - 1];
        if (direction === 'left') {
          const btn = playerModal.querySelector<HTMLElement>('#player-quality-button');
          if (btn) return btn;
        }
        return currentEl;
      }
      return dialogItems[1] || dialogItems[0] || currentEl;
    }

    // 3. Sleep Timer Dialog Dropdown
    const sleepDialog = playerModal.querySelector<HTMLElement>('#player-dialog-sleep-timer');
    if (sleepDialog && sleepDialog.getBoundingClientRect().height > 0) {
      const dialogItems = Array.from(
        sleepDialog.querySelectorAll<HTMLElement>('#modal-close-sleep, [id^="modal-sleep-"]')
      ).filter(el => el.getBoundingClientRect().height > 0);

      const idx = dialogItems.indexOf(currentEl);
      if (idx !== -1) {
        if (direction === 'down' && idx < dialogItems.length - 1) return dialogItems[idx + 1];
        if (direction === 'up' && idx > 0) return dialogItems[idx - 1];
        if (direction === 'left') {
          const btn = playerModal.querySelector<HTMLElement>('#player-sleep-timer-button');
          if (btn) return btn;
        }
        return currentEl;
      }
      return dialogItems[1] || dialogItems[0] || currentEl;
    }

    // 4. Episodes Side Drawer
    const drawer = playerModal.querySelector<HTMLElement>('#player-episodes-drawer');
    if (drawer && drawer.getBoundingClientRect().width > 0) {
      const drawerItems = Array.from(
        drawer.querySelectorAll<HTMLElement>(
          '#close-episodes-drawer-button, [id^="player-drawer-season-"], #player-drawer-search-input, [id^="player-drawer-ep-"]'
        )
      ).filter(el => el.getBoundingClientRect().height > 0);

      const idx = drawerItems.indexOf(currentEl);
      if (idx !== -1) {
        if (direction === 'down' && idx < drawerItems.length - 1) return drawerItems[idx + 1];
        if (direction === 'up' && idx > 0) return drawerItems[idx - 1];
        if (direction === 'left') {
          const drawerToggle = playerModal.querySelector<HTMLElement>('#toggle-episodes-drawer');
          if (drawerToggle) return drawerToggle;
        }
        return currentEl; // Trap inside drawer
      }
      // If entering drawer, find the active playing episode or first episode
      const activeEp = drawer.querySelector<HTMLElement>('[id^="player-drawer-ep-"].bg-amber-500\\/20') ||
        drawer.querySelector<HTMLElement>('[id^="player-drawer-ep-"]');
      if (activeEp) return activeEp;
      return drawerItems[0] || currentEl;
    }

    // Always wake controls when navigating in player
    if (typeof (window as any).__videoPlayerWakeControls === 'function') {
      (window as any).__videoPlayerWakeControls();
    }

    // Top Bar Buttons
    const topButtons = Array.from(
      playerModal.querySelectorAll<HTMLElement>(
        '#close-player-button, #player-audio-subs-button, #player-quality-button, #player-sleep-timer-button, #player-aspect-button, #skip-intro-button, #player-pip-button, #toggle-episodes-drawer'
      )
    ).filter(el => el.getBoundingClientRect().width > 0);

    // Scrubber
    const scrubber = playerModal.querySelector<HTMLElement>('#player-scrubber-input');

    // Bottom Bar Buttons
    const bottomButtons = Array.from(
      playerModal.querySelectorAll<HTMLElement>(
        '#player-play-button, #player-prev-ep-button, #player-rewind-button, #player-forward-button, #player-next-ep-button, #player-mute-button, #player-volume-input, #player-dialogue-boost-button, [id^="player-speed-"], #player-fullscreen-button'
      )
    ).filter(el => el.getBoundingClientRect().width > 0);

    // 1. Inside Top Bar
    const topIdx = topButtons.indexOf(currentEl);
    if (topIdx !== -1) {
      if (direction === 'left' && topIdx > 0) return topButtons[topIdx - 1];
      if (direction === 'right' && topIdx < topButtons.length - 1) return topButtons[topIdx + 1];
      if (direction === 'down') {
        if (scrubber) return scrubber;
        if (bottomButtons.length > 0) return bottomButtons[0];
      }
      return currentEl;
    }

    // 2. On Scrubber
    if (currentEl.id === 'player-scrubber-input') {
      if (direction === 'up') {
        if (topButtons.length > 0) return topButtons[0];
      }
      if (direction === 'down') {
        const playBtn = playerModal.querySelector<HTMLElement>('#player-play-button') || bottomButtons[0];
        if (playBtn) return playBtn;
      }
      return currentEl;
    }

    // 3. Inside Bottom Bar
    const botIdx = bottomButtons.indexOf(currentEl);
    if (botIdx !== -1) {
      if (direction === 'left' && botIdx > 0) return bottomButtons[botIdx - 1];
      if (direction === 'right' && botIdx < bottomButtons.length - 1) return bottomButtons[botIdx + 1];
      if (direction === 'up') {
        if (scrubber) return scrubber;
        if (topButtons.length > 0) return topButtons[0];
      }
      return currentEl;
    }

    // If focus is on backdrop or outside, focus primary play button
    const defaultPlayBtn = playerModal.querySelector<HTMLElement>('#player-play-button');
    if (defaultPlayBtn) return defaultPlayBtn;

    return null;
  }, []);

  // Dedicated Media Detail Modal Navigation Handler (Pop-up with Episode Guide & Actions)
  const handleMediaDetailModalNavigation = useCallback((
    currentEl: HTMLElement,
    direction: 'up' | 'down' | 'left' | 'right'
  ): HTMLElement | null => {
    const modal = document.querySelector<HTMLElement>('#media-detail-modal');
    if (!modal) return null;

    const isInside = modal.contains(currentEl);
    if (!isInside) {
      const primaryTarget = modal.querySelector<HTMLElement>('#modal-play-primary, #close-modal-button');
      return primaryTarget;
    }

    // Action buttons: Play, Watchlist, SubDL, Share
    const actionButtons = Array.from(
      modal.querySelectorAll<HTMLElement>('#modal-play-primary, #modal-watchlist-toggle, #modal-subdl-button, #modal-share-button')
    ).filter(el => el.getBoundingClientRect().width > 0);

    // Season selector buttons: #modal-season-1, #modal-season-2, ...
    const seasonButtons = Array.from(
      modal.querySelectorAll<HTMLElement>('[id^="modal-season-"]')
    ).filter(el => el.getBoundingClientRect().width > 0);

    // Episode row elements or cards
    const episodeRows = Array.from(
      modal.querySelectorAll<HTMLElement>('[id^="modal-episode-row-"], [id^="modal-episode-card-"]')
    ).filter(el => el.getBoundingClientRect().height > 0);

    const closeBtn = modal.querySelector<HTMLElement>('#close-modal-button');

    // 1. Close Button in top right
    if (currentEl.id === 'close-modal-button') {
      if (direction === 'down' || direction === 'left') {
        if (actionButtons.length > 0) return actionButtons[0];
      }
      return currentEl; // Trap inside
    }

    // Check if current element is an internal episode button (Play, SubDL, Info)
    const isEpPlay = currentEl.id.startsWith('modal-ep-play-');
    const isEpSub = currentEl.id.startsWith('modal-ep-sub-');
    const isEpInfo = currentEl.id.startsWith('modal-ep-info-');

    if (isEpPlay || isEpSub || isEpInfo) {
      // Extract episode index
      const match = currentEl.id.match(/^modal-ep-(?:play|sub|info)-(\d+)$/);
      const epIdx = match ? parseInt(match[1], 10) : 0;

      // Inner buttons order in each row: Play -> SubDL -> Info
      const currentType = isEpPlay ? 'play' : isEpSub ? 'sub' : 'info';

      if (direction === 'left') {
        if (currentType === 'info') {
          const subBtn = modal.querySelector<HTMLElement>(`#modal-ep-sub-${epIdx}`);
          if (subBtn) return subBtn;
        }
        if (currentType === 'sub') {
          const playBtn = modal.querySelector<HTMLElement>(`#modal-ep-play-${epIdx}`);
          if (playBtn) return playBtn;
        }
        if (currentType === 'play') {
          // Move from right column (episodes) to left column (action buttons)
          if (actionButtons.length > 0) {
            return actionButtons[0];
          }
        }
        return currentEl;
      }

      if (direction === 'right') {
        if (currentType === 'play') {
          const subBtn = modal.querySelector<HTMLElement>(`#modal-ep-sub-${epIdx}`);
          if (subBtn) return subBtn;
        }
        if (currentType === 'sub') {
          const infoBtn = modal.querySelector<HTMLElement>(`#modal-ep-info-${epIdx}`);
          if (infoBtn) return infoBtn;
        }
        if (currentType === 'info') {
          if (closeBtn) return closeBtn;
        }
        return currentEl;
      }

      if (direction === 'up') {
        if (epIdx > 0) {
          const targetEpIdx = epIdx - 1;
          const targetBtn = modal.querySelector<HTMLElement>(`#modal-ep-${currentType}-${targetEpIdx}`) ||
                            modal.querySelector<HTMLElement>(`#modal-ep-play-${targetEpIdx}`);
          if (targetBtn) {
            targetBtn.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            return targetBtn;
          }
        } else {
          // At the very first episode: go up to season button or close button
          if (seasonButtons.length > 0) {
            const activeSeason = seasonButtons.find(s => s.classList.contains('bg-sky-500') || s.classList.contains('bg-amber-500')) || seasonButtons[0];
            return activeSeason;
          }
          if (closeBtn) return closeBtn;
        }
        return currentEl;
      }

      if (direction === 'down') {
        const nextEpIdx = epIdx + 1;
        const targetBtn = modal.querySelector<HTMLElement>(`#modal-ep-${currentType}-${nextEpIdx}`) ||
                          modal.querySelector<HTMLElement>(`#modal-ep-play-${nextEpIdx}`);
        if (targetBtn) {
          targetBtn.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          return targetBtn;
        }
        return currentEl; // Stay on last episode button
      }
    }

    // 2. Action buttons (Play, SubDL, Share in Left Panel or Modal Header)
    const actionIdx = actionButtons.indexOf(currentEl);
    if (actionIdx !== -1) {
      if (direction === 'left') {
        if (actionIdx > 0) return actionButtons[actionIdx - 1];
        return currentEl;
      }
      if (direction === 'right') {
        if (actionIdx < actionButtons.length - 1) {
          return actionButtons[actionIdx + 1];
        }
        // If at the end of action buttons, jump to the Right Panel (Season tabs or first episode play button)
        if (seasonButtons.length > 0) {
          const activeSeason = seasonButtons.find(s => s.classList.contains('bg-sky-500') || s.classList.contains('bg-amber-500')) || seasonButtons[0];
          return activeSeason;
        }
        const firstEpPlay = modal.querySelector<HTMLElement>('#modal-ep-play-0');
        if (firstEpPlay) {
          firstEpPlay.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          return firstEpPlay;
        }
        if (closeBtn) return closeBtn;
        return currentEl;
      }
      if (direction === 'up') {
        if (closeBtn) return closeBtn;
        return currentEl;
      }
      if (direction === 'down') {
        // In side-by-side or movie layout:
        // If there are season buttons or episodes below, navigate to them
        if (seasonButtons.length > 0) {
          const activeSeason = seasonButtons.find(s => s.classList.contains('bg-sky-500') || s.classList.contains('bg-amber-500')) || seasonButtons[0];
          return activeSeason;
        }
        const firstEpPlay = modal.querySelector<HTMLElement>('#modal-ep-play-0');
        if (firstEpPlay) {
          firstEpPlay.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          return firstEpPlay;
        }
        return currentEl;
      }
    }

    // 3. Season selector buttons
    const seasonIdx = seasonButtons.indexOf(currentEl);
    if (seasonIdx !== -1) {
      if (direction === 'left') {
        if (seasonIdx > 0) return seasonButtons[seasonIdx - 1];
        // Move to left panel action buttons
        if (actionButtons.length > 0) return actionButtons[0];
        return currentEl;
      }
      if (direction === 'right') {
        if (seasonIdx < seasonButtons.length - 1) return seasonButtons[seasonIdx + 1];
        if (closeBtn) return closeBtn;
        return currentEl;
      }
      if (direction === 'up') {
        if (closeBtn) return closeBtn;
        if (actionButtons.length > 0) return actionButtons[0];
        return currentEl;
      }
      if (direction === 'down') {
        const firstEpPlay = modal.querySelector<HTMLElement>('#modal-ep-play-0');
        if (firstEpPlay) {
          firstEpPlay.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          return firstEpPlay;
        }
        return currentEl;
      }
    }

    // Fallback: If focus somehow ends on an episode row card container
    const epRowIdx = episodeRows.indexOf(currentEl);
    if (epRowIdx !== -1) {
      const playBtn = modal.querySelector<HTMLElement>(`#modal-ep-play-${epRowIdx}`);
      if (playBtn) return playBtn;
    }

    return null;
  }, []);

  // Dedicated Navbar Navigation Handler
  const handleNavbarNavigation = useCallback((
    currentEl: HTMLElement,
    direction: 'up' | 'down' | 'left' | 'right'
  ): HTMLElement | null => {
    const navbar = document.querySelector('#main-navbar, header');
    if (!navbar) return null;

    // Check if dropdowns are open
    const userDropdown = document.querySelector<HTMLElement>('#user-dropdown-menu');
    const colDropdown = document.querySelector<HTMLElement>('#collections-dropdown-menu');

    const navbarFocusables = Array.from(
      navbar.querySelectorAll<HTMLElement>('[data-focusable="true"], button:not([disabled]), a[href], input:not([disabled])')
    ).filter(el => {
      // Exclude dropdown items from horizontal navbar scan
      if (el.closest('#user-dropdown-menu') || el.closest('#collections-dropdown-menu')) return false;
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });

    const currentIndex = navbarFocusables.indexOf(currentEl);

    // 1. Moving Left / Right within the Navbar: Pure deterministic sequential jump
    if (direction === 'right') {
      if (currentIndex >= 0 && currentIndex < navbarFocusables.length - 1) {
        return navbarFocusables[currentIndex + 1];
      }
      return null;
    }

    if (direction === 'left') {
      if (currentIndex > 0) {
        return navbarFocusables[currentIndex - 1];
      }
      return null;
    }

    // 2. Moving Down from the Navbar into Dropdowns or Main Screen:
    if (direction === 'down') {
      // If collections dropdown is open and we are on its trigger:
      if (currentEl.id === 'nav-collections-dropdown' && colDropdown) {
        const firstDrop = colDropdown.querySelector<HTMLElement>('[data-focusable="true"], button:not([disabled])');
        if (firstDrop) {
          firstDrop.scrollIntoView({ block: 'nearest' });
          return firstDrop;
        }
      }

      // If user profile dropdown is open and we are on its trigger:
      if (currentEl.id === 'user-profile-button' && userDropdown) {
        const firstDrop = userDropdown.querySelector<HTMLElement>('[data-focusable="true"], button:not([disabled])');
        if (firstDrop) {
          firstDrop.scrollIntoView({ block: 'nearest' });
          return firstDrop;
        }
      }

      // Look for search input if search drawer is open
      const searchInput = document.querySelector<HTMLElement>('#search-input-field');
      if (searchInput && searchInput.getBoundingClientRect().height > 0) {
        return searchInput;
      }

      // Look for hero primary buttons first
      const heroPlay = document.querySelector<HTMLElement>('#hero-play-button');
      if (heroPlay && heroPlay.getBoundingClientRect().height > 0) {
        return heroPlay;
      }

      // Or any primary hero action
      const heroFocusable = document.querySelector<HTMLElement>('#hero-banner [data-focusable="true"]');
      if (heroFocusable && heroFocusable.getBoundingClientRect().height > 0) {
        return heroFocusable;
      }

      // Or the first focusable element inside the main content area
      const main = document.querySelector('main, #root');
      if (main) {
        const mainFocusables = Array.from(
          main.querySelectorAll<HTMLElement>('[data-focusable="true"], button:not([disabled]), a[href]')
        ).filter(el => {
          if (el.closest('#main-navbar, header')) return false;
          const rect = el.getBoundingClientRect();
          return rect.top > 60 && rect.width > 0 && rect.height > 0;
        });

        if (mainFocusables.length > 0) {
          const currentRect = currentEl.getBoundingClientRect();
          const currentX = currentRect.left + currentRect.width / 2;

          let bestEl = mainFocusables[0];
          let bestDx = Infinity;

          for (const el of mainFocusables.slice(0, 8)) {
            const rect = el.getBoundingClientRect();
            const elX = rect.left + rect.width / 2;
            const dx = Math.abs(elX - currentX);
            if (dx < bestDx) {
              bestDx = dx;
              bestEl = el;
            }
          }
          return bestEl;
        }
      }
    }

    return null;
  }, []);

  // Dedicated Instant 2D Matrix Grid Navigation Handler (Zero-Reflow Deterministic Grid)
  const handleGridNavigation = useCallback((
    currentEl: HTMLElement,
    direction: 'up' | 'down' | 'left' | 'right'
  ): HTMLElement | null => {
    const gridContainer = currentEl.closest<HTMLElement>('.grid, [data-grid="true"]');
    if (!gridContainer) return null;

    // Do not interfere if we are in video player modal or navigation header
    if (gridContainer.closest('#video-player-modal, #main-navbar, header')) return null;

    // Collect all primary focusable elements in this grid
    const focusableChildren = Array.from(
      gridContainer.querySelectorAll<HTMLElement>(
        '[data-focusable="true"]:not([tabindex="-1"]), [id^="media-card-"], [id^="collection-card-"]'
      )
    ).filter(el => {
      // Exclude sub-elements inside another focusable card
      const parentCard = el.parentElement?.closest('[data-focusable="true"]');
      if (parentCard && parentCard !== el && gridContainer.contains(parentCard)) return false;
      return true;
    });

    const curIdx = focusableChildren.indexOf(currentEl);
    if (curIdx === -1) return null;

    // Determine column count without layout thrashing
    const explicitCols = parseInt(gridContainer.getAttribute('data-columns') || '0', 10);
    let cols = explicitCols > 0 ? explicitCols : 0;

    if (cols === 0 && focusableChildren.length > 1) {
      // Fast check using offsetTop (does not trigger layout recalculation like getBoundingClientRect)
      const firstTop = focusableChildren[0].offsetTop;
      for (let i = 1; i < Math.min(focusableChildren.length, 12); i++) {
        if (Math.abs(focusableChildren[i].offsetTop - firstTop) > 20) {
          cols = i;
          break;
        }
      }
    }

    if (cols <= 0) {
      // Fallback based on typical TV / desktop landscape breakpoints
      cols = window.innerWidth >= 1024 ? 5 : (window.innerWidth >= 640 ? 3 : 2);
    }

    // 1. Horizontal Matrix Navigation (Immediate Prev / Next)
    if (direction === 'left') {
      if (curIdx > 0) {
        return focusableChildren[curIdx - 1];
      }
      return null; // Left edge of grid: allow natural fallback or boundaries
    }

    if (direction === 'right') {
      if (curIdx < focusableChildren.length - 1) {
        return focusableChildren[curIdx + 1];
      }
      return null; // Right edge of grid
    }

    // 2. Vertical Matrix Navigation (Immediate +/- cols step, exactly like Compose chunked(5))
    if (direction === 'down') {
      const targetIdx = curIdx + cols;
      if (targetIdx < focusableChildren.length) {
        return focusableChildren[targetIdx];
      }
      // If past the last item, but not on the last row, snap to the very last card
      const lastRowStart = Math.floor((focusableChildren.length - 1) / cols) * cols;
      if (curIdx < lastRowStart) {
        return focusableChildren[focusableChildren.length - 1];
      }
      // Bottom row: let navigator transition out to next section or auto-load
      return null;
    }

    if (direction === 'up') {
      const targetIdx = curIdx - cols;
      if (targetIdx >= 0) {
        return focusableChildren[targetIdx];
      }
      // Top row: let navigator transition out to breadcrumbs, previous section or navbar
      return null;
    }

    return null;
  }, []);

  // Find geometrically closest element in direction
  const findNextElement = useCallback((
    currentEl: HTMLElement,
    direction: 'up' | 'down' | 'left' | 'right'
  ): HTMLElement | null => {
    // 0. Video Player Modal (Streaming Player)
    const videoPlayerModal = document.querySelector<HTMLElement>('#video-player-modal');
    if (videoPlayerModal) {
      const playerTarget = handleVideoPlayerNavigation(currentEl, direction);
      if (playerTarget) return playerTarget;
      return currentEl; // STRICT FOCUS TRAP: never escape outside video player modal
    }

    // 0.05 Episode Detail Modal (Full Episode Metadata Sheet)
    const epDetailModal = document.querySelector<HTMLElement>('#episode-detail-modal');
    if (epDetailModal) {
      const isInsideEp = epDetailModal.contains(currentEl);
      const epButtons = Array.from(
        epDetailModal.querySelectorAll<HTMLElement>('#episode-detail-close-btn, #episode-detail-sub-btn, #episode-detail-play-btn')
      ).filter(el => el.getBoundingClientRect().width > 0);

      if (!isInsideEp) {
        return epDetailModal.querySelector<HTMLElement>('#episode-detail-play-btn, #episode-detail-close-btn');
      }

      if (currentEl.id === 'episode-detail-close-btn') {
        if (direction === 'down' || direction === 'left') {
          return epDetailModal.querySelector<HTMLElement>('#episode-detail-play-btn') || epButtons[0];
        }
        return currentEl;
      }

      if (currentEl.id === 'episode-detail-sub-btn') {
        if (direction === 'right') return epDetailModal.querySelector<HTMLElement>('#episode-detail-play-btn') || currentEl;
        if (direction === 'up') return epDetailModal.querySelector<HTMLElement>('#episode-detail-close-btn') || currentEl;
        return currentEl;
      }

      if (currentEl.id === 'episode-detail-play-btn') {
        if (direction === 'left') return epDetailModal.querySelector<HTMLElement>('#episode-detail-sub-btn') || currentEl;
        if (direction === 'up') return epDetailModal.querySelector<HTMLElement>('#episode-detail-close-btn') || currentEl;
        return currentEl;
      }

      return currentEl;
    }

    // 0.1 Media Detail Modal (Movie & Series Details Pop-up)
    const mediaModal = document.querySelector<HTMLElement>('#media-detail-modal');
    if (mediaModal) {
      const modalTarget = handleMediaDetailModalNavigation(currentEl, direction);
      if (modalTarget) return modalTarget;
      return currentEl; // STRICT FOCUS TRAP: never escape outside media detail modal
    }

    // 0.2 Generic Active Modal Dialog Trap
    const activeModal = document.querySelector<HTMLElement>(
      '[role="dialog"], #video-player-modal, #auth-modal, #admin-dashboard-modal, #profile-modal, #shortcuts-modal, #device-mode-modal, .modal-dialog'
    );
    if (activeModal && !activeModal.contains(currentEl)) {
      const firstFocusable = activeModal.querySelector<HTMLElement>('[data-focusable="true"], button:not([disabled]), input:not([disabled])');
      if (firstFocusable) return firstFocusable;
    }

    // 0.2 Search drawer fast path
    const searchTarget = handleSearchDrawerNavigation(currentEl, direction);
    if (searchTarget) return searchTarget;

    // 0.3 Check if any dropdown menu is active (IPTV Source, IPTV Category, Collections, User profile)
    const openDropdown = document.querySelector<HTMLElement>(
      '#iptv-category-dropdown-menu, #iptv-source-dropdown-menu, #collections-dropdown-menu, #user-dropdown-menu'
    );
    const isTriggerBtn = (
      currentEl.id === 'iptv-category-selector-btn' ||
      currentEl.id === 'iptv-source-selector-btn' ||
      currentEl.id === 'nav-collections-dropdown' ||
      currentEl.id === 'user-profile-button'
    );
    const isInsideAnyDropdown = Boolean(openDropdown && openDropdown.contains(currentEl));

    if (openDropdown && (isInsideAnyDropdown || isTriggerBtn)) {
      const dropTarget = handleDropdownNavigation(currentEl, direction);
      if (dropTarget) return dropTarget;
      if (isInsideAnyDropdown) {
        return currentEl; // STRICT TRAP: never escape open dropdown into background page elements
      }
    }

    // 2. Fast-path: If inside navbar, use instantaneous deterministic navigation
    const isInNavbar = Boolean(currentEl.closest('#main-navbar, header'));
    if (isInNavbar) {
      const navTarget = handleNavbarNavigation(currentEl, direction);
      if (navTarget) return navTarget;
    }

    // 2.1 Fast-path: Instant 2D Matrix Grid Navigation (Jetpack Compose Chunked Pattern)
    const gridTarget = handleGridNavigation(currentEl, direction);
    if (gridTarget) return gridTarget;

    const focusables = getFocusableElements();
    if (focusables.length === 0) return null;

    // Single-pass Rect Cache to prevent Layout Thrashing on TV hardware
    const rectCache = new Map<HTMLElement, DOMRect>();
    const getCachedRect = (el: HTMLElement): DOMRect => {
      let r = rectCache.get(el);
      if (!r) {
        r = el.getBoundingClientRect();
        rectCache.set(el, r);
      }
      return r;
    };

    const currentRect = getCachedRect(currentEl);
    const currentCenter = {
      x: currentRect.left + currentRect.width / 2,
      y: currentRect.top + currentRect.height / 2
    };

    // 3. Fast-path: Sibling card navigation inside same horizontal container or row
    if (direction === 'left' || direction === 'right') {
      const parentRow = currentEl.closest('.overflow-x-auto, [data-row="true"], [id^="row-"]');
      if (parentRow) {
        // Collect only top-level focusable items in this row
        const rowItems = Array.from(
          parentRow.querySelectorAll<HTMLElement>('[data-focusable="true"]:not([tabindex="-1"]), [id^="media-card-"]')
        ).filter(el => {
          if (el.closest('#main-navbar, header')) return false;
          // Filter out sub-elements inside another card
          const parentCard = el.parentElement?.closest('[data-focusable="true"]');
          if (parentCard && parentCard !== el) return false;
          const r = getCachedRect(el);
          return r.width > 0 && r.height > 0;
        });

        const curIdx = rowItems.indexOf(currentEl);
        if (curIdx !== -1) {
          if (direction === 'right' && curIdx < rowItems.length - 1) {
            return rowItems[curIdx + 1];
          }
          if (direction === 'left' && curIdx > 0) {
            return rowItems[curIdx - 1];
          }
        }
      }
    }

    // 4. Moving UP: Strict card-by-card / row-by-row until reaching the top
    if (direction === 'up') {
      // Find all interactive items strictly above currentEl that are NOT inside the navbar
      const nonNavbarAbove = focusables.filter(el => {
        if (el === currentEl) return false;
        if (el.closest('#main-navbar, header')) return false;
        const r = getCachedRect(el);
        const center = r.top + r.height / 2;
        // Must be clearly above current element
        return r.bottom <= currentRect.top + 8 || center < currentCenter.y - 15;
      });

      if (nonNavbarAbove.length > 0) {
        // Find minimum distance in Y to find items in the row immediately above
        let minDy = Infinity;
        for (const el of nonNavbarAbove) {
          const r = getCachedRect(el);
          const centerY = r.top + r.height / 2;
          const dy = currentCenter.y - centerY;
          if (dy > 0 && dy < minDy) {
            minDy = dy;
          }
        }

        // Filter elements in the immediate row above (within 60px of the closest Y)
        const immediateRowAbove = nonNavbarAbove.filter(el => {
          const r = getCachedRect(el);
          const centerY = r.top + r.height / 2;
          const dy = currentCenter.y - centerY;
          return dy >= 0 && dy <= minDy + 60;
        });

        // Among those, pick the one horizontally closest to currentCenter.x
        let bestCandidate: HTMLElement | null = null;
        let minDx = Infinity;

        for (const el of immediateRowAbove) {
          const r = getCachedRect(el);
          const centerX = r.left + r.width / 2;
          const dx = Math.abs(currentCenter.x - centerX);
          if (dx < minDx) {
            minDx = dx;
            bestCandidate = el;
          }
        }

        if (bestCandidate) {
          return bestCandidate;
        }
      }

      // ONLY when there are no content elements above AND no active modal, transition to the navbar
      if (!activeModal && !mediaModal) {
        const navbar = document.querySelector('#main-navbar, header');
        if (navbar) {
          // Target active view button or #nav-home
          const activeNavBtn = navbar.querySelector<HTMLElement>(
            '#nav-home.text-sky-400, #nav-series.text-sky-400, #nav-movies.text-sky-400, #nav-collections.text-sky-400, #nav-watchlist.text-sky-400, #nav-search.text-sky-400, #nav-home.text-amber-400, #nav-series.text-amber-400, #nav-movies.text-amber-400, #nav-collections.text-amber-400, #nav-watchlist.text-amber-400, #nav-search.text-amber-400'
          );
          if (activeNavBtn && getCachedRect(activeNavBtn).height > 0) {
            return activeNavBtn;
          }

          const navHome = navbar.querySelector<HTMLElement>('#nav-home');
          if (navHome && getCachedRect(navHome).height > 0) {
            return navHome;
          }

          const firstNav = navbar.querySelector<HTMLElement>('[data-focusable="true"], button:not([disabled])');
          if (firstNav) return firstNav;
        }
      }
      return null;
    }

    // 5. Moving DOWN: Move strictly to the immediate row / section below
    if (direction === 'down') {
      const nonNavbarBelow = focusables.filter(el => {
        if (el === currentEl) return false;
        if (el.closest('#main-navbar, header')) return false;
        const r = getCachedRect(el);
        const center = r.top + r.height / 2;
        // Must be clearly below current element
        return r.top >= currentRect.bottom - 8 || center > currentCenter.y + 15;
      });

      if (nonNavbarBelow.length > 0) {
        // Find minimum distance in Y to find items in the row immediately below
        let minDy = Infinity;
        for (const el of nonNavbarBelow) {
          const r = getCachedRect(el);
          const centerY = r.top + r.height / 2;
          const dy = centerY - currentCenter.y;
          if (dy > 0 && dy < minDy) {
            minDy = dy;
          }
        }

        // Filter elements in the immediate row below (within 60px of the closest Y)
        const immediateRowBelow = nonNavbarBelow.filter(el => {
          const r = getCachedRect(el);
          const centerY = r.top + r.height / 2;
          const dy = centerY - currentCenter.y;
          return dy >= 0 && dy <= minDy + 60;
        });

        // Among those, pick the one horizontally closest to currentCenter.x
        let bestCandidate: HTMLElement | null = null;
        let minDx = Infinity;

        for (const el of immediateRowBelow) {
          const r = getCachedRect(el);
          const centerX = r.left + r.width / 2;
          const dx = Math.abs(currentCenter.x - centerX);
          if (dx < minDx) {
            minDx = dx;
            bestCandidate = el;
          }
        }

        if (bestCandidate) {
          return bestCandidate;
        }
      }
      return null;
    }

    // 6. Generic Geometric Fallback for Left/Right
    let bestCandidate: HTMLElement | null = null;
    let minDistance = Infinity;

    for (const el of focusables) {
      if (el === currentEl) continue;
      if (el.closest('#main-navbar, header')) continue;

      const rect = getCachedRect(el);
      const center = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      };

      const dx = center.x - currentCenter.x;
      const dy = center.y - currentCenter.y;

      let isValidDirection = false;
      let primaryDistance = 0;
      let orthogonalDistance = 0;

      if (direction === 'right') {
        isValidDirection = dx > 8 && Math.abs(dy) < 140;
        primaryDistance = dx;
        orthogonalDistance = Math.abs(dy);
      } else if (direction === 'left') {
        isValidDirection = dx < -8 && Math.abs(dy) < 140;
        primaryDistance = Math.abs(dx);
        orthogonalDistance = Math.abs(dy);
      }

      if (isValidDirection) {
        const totalDistance = primaryDistance + orthogonalDistance * 2.0;
        if (totalDistance < minDistance) {
          minDistance = totalDistance;
          bestCandidate = el;
        }
      }
    }

    return bestCandidate;
  }, [getFocusableElements, handleNavbarNavigation, handleGridNavigation, handleDropdownNavigation, handleSearchDrawerNavigation, handleMediaDetailModalNavigation, handleVideoPlayerNavigation]);

  // Focus element helper with high-performance instant scroll
  const focusElement = useCallback((el: HTMLElement | null) => {
    if (!el) return;

    // Remove focus class from previous focused element
    if (currentFocusedRef.current && currentFocusedRef.current !== el) {
      currentFocusedRef.current.classList.remove('tv-focused');
    }

    currentFocusedRef.current = el;
    el.focus({ preventScroll: true });
    el.classList.add('tv-focused');

    // Inside video player modal: keep controls awake and active
    const playerModal = el.closest('#video-player-modal');
    if (playerModal) {
      if (typeof (window as any).__videoPlayerWakeControls === 'function') {
        (window as any).__videoPlayerWakeControls();
      }
      return;
    }

    // Inside a modal popup or scrollable dropdown (e.g. MediaDetailModal, IPTV dropdowns): scroll within container and do not scroll main window
    const dropdownOrModalParent = el.closest(
      '#media-detail-modal, [role="dialog"], .modal-dialog, #auth-modal, #admin-dashboard-modal, #iptv-category-dropdown-menu, #iptv-source-dropdown-menu, #collections-dropdown-menu, #user-dropdown-menu, #iptv-channel-guide-drawer'
    );
    if (dropdownOrModalParent) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
      return;
    }

    const isInNavbar = Boolean(el.closest('#main-navbar, header'));
    if (isInNavbar) {
      // When focusing the top navbar, ensure whole window is scrolled completely to the top
      window.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }

    // Inside horizontal scrolling rows: center the focused card
    const scrollParent = el.closest('.overflow-x-auto, [data-row="true"]');
    if (scrollParent) {
      const pRect = scrollParent.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      if (elRect.left < pRect.left + 50) {
        scrollParent.scrollBy({ left: elRect.left - pRect.left - 80, behavior: 'auto' });
      } else if (elRect.right > pRect.right - 50) {
        scrollParent.scrollBy({ left: elRect.right - pRect.right + 80, behavior: 'auto' });
      }
    }

    // Vertical viewport positioning: ensure elements are NEVER covered by the sticky navbar (~72px)
    const navbarHeight = 85;
    const elRect = el.getBoundingClientRect();
    const isTopSection = Boolean(
      el.closest('#hero-banner, .hero-section') || 
      el.id.startsWith('hero-') || 
      el.id.startsWith('genre-pill-')
    );

    if (isTopSection) {
      // Reveal the entire hero banner and top header without any clipping
      window.scrollTo({ top: 0, behavior: 'auto' });
    } else if (elRect.top < navbarHeight + 15) {
      // Element is behind or too close to sticky navbar: scroll up with generous clearance
      window.scrollBy({ top: elRect.top - navbarHeight - 35, behavior: 'auto' });
    } else if (elRect.bottom > window.innerHeight - 30) {
      // Element is partially off the bottom: scroll down
      window.scrollBy({ top: elRect.bottom - window.innerHeight + 60, behavior: 'auto' });
    }

    // Predictive auto-loading when navigating cards near the end of the grid
    if (el.id && el.id.startsWith('media-card-')) {
      if (typeof (window as any).__loadMoreItems === 'function') {
        const cards = Array.from(document.querySelectorAll<HTMLElement>('[id^="media-card-"]'));
        const idx = cards.indexOf(el);
        if (idx !== -1 && idx >= cards.length - 10) {
          (window as any).__loadMoreItems();
        }
      }
    }
  }, []);

  // Primary directional navigator
  const navigateDirection = useCallback((direction: 'up' | 'down' | 'left' | 'right') => {
    const current = (document.activeElement as HTMLElement) || currentFocusedRef.current;
    const playerModal = document.querySelector<HTMLElement>('#video-player-modal');
    
    if (playerModal) {
      const isAnyModalOpen = Boolean(
        playerModal.querySelector('#player-dialog-audio-subs') ||
        playerModal.querySelector('#player-dialog-quality') ||
        playerModal.querySelector('#player-dialog-sleep-timer') ||
        playerModal.querySelector('#player-episodes-drawer') ||
        playerModal.querySelector('#player-countdown-overlay')
      );

      if (!isAnyModalOpen) {
        if (direction === 'left' && (!current || current === document.body || current.id === 'video-player-modal')) {
          if (typeof (window as any).__videoPlayerSeekBy === 'function') {
            (window as any).__videoPlayerSeekBy(-10);
            (window as any).__videoPlayerWakeControls?.();
            const btn = playerModal.querySelector<HTMLElement>('#player-play-button');
            if (btn) focusElement(btn);
            return;
          }
        }
        if (direction === 'right' && (!current || current === document.body || current.id === 'video-player-modal')) {
          if (typeof (window as any).__videoPlayerSeekBy === 'function') {
            (window as any).__videoPlayerSeekBy(10);
            (window as any).__videoPlayerWakeControls?.();
            const btn = playerModal.querySelector<HTMLElement>('#player-play-button');
            if (btn) focusElement(btn);
            return;
          }
        }
      }
    }

    if (!current || !document.body.contains(current) || current === document.body) {
      // Initial focus: grab first visible focusable element
      const focusables = getFocusableElements();
      if (focusables.length > 0) {
        focusElement(focusables[0]);
      }
      return;
    }

    const nextEl = findNextElement(current, direction);
    if (nextEl) {
      focusElement(nextEl);
    } else if (direction === 'down') {
      // If at the bottom of the grid, trigger automatic loading of more items
      if (typeof (window as any).__loadMoreItems === 'function') {
        (window as any).__loadMoreItems();
        setTimeout(() => {
          const updated = findNextElement(current, 'down');
          if (updated) focusElement(updated);
        }, 120);
      }
    }
  }, [findNextElement, focusElement, getFocusableElements]);

  // Primary Action Trigger
  const triggerEnterAction = useCallback(() => {
    const current = (document.activeElement as HTMLElement) || currentFocusedRef.current;
    const playerModal = document.querySelector<HTMLElement>('#video-player-modal');
    
    if (playerModal) {
      const isAnyModalOpen = Boolean(
        playerModal.querySelector('#player-dialog-audio-subs') ||
        playerModal.querySelector('#player-dialog-quality') ||
        playerModal.querySelector('#player-dialog-sleep-timer') ||
        playerModal.querySelector('#player-episodes-drawer') ||
        playerModal.querySelector('#player-countdown-overlay')
      );

      const isInteractiveBtn = current && (
        current.tagName === 'BUTTON' || 
        current.tagName === 'INPUT' || 
        current.getAttribute('data-focusable') === 'true'
      ) && playerModal.contains(current);

      if (!isAnyModalOpen && (!isInteractiveBtn || current === playerModal || current === document.body)) {
        if (typeof (window as any).__videoPlayerTogglePlay === 'function') {
          (window as any).__videoPlayerTogglePlay();
          (window as any).__videoPlayerWakeControls?.();
          return;
        }
      }
    }

    if (current && current.tagName !== 'INPUT' && current.tagName !== 'TEXTAREA') {
      if (onEnter) {
        onEnter(current);
      }
      current.click();
    }
  }, [onEnter]);

  // Primary Back Trigger
  const triggerBackAction = useCallback(() => {
    const active = document.activeElement as HTMLElement | null;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) {
      active.blur();
    }

    // 1. Check IPTV-specific back handling first
    if (typeof (window as any).__handleIptvBack === 'function') {
      const handled = (window as any).__handleIptvBack();
      if (handled) return;
    }

    // 2. Check main video player back handling
    if (typeof (window as any).__handleVideoPlayerBack === 'function') {
      const handled = (window as any).__handleVideoPlayerBack();
      if (handled) return;
    }

    // 3. Fallback to App level back handler
    if (onBack) {
      onBack();
    }
  }, [onBack]);

  // 1. Keyboard & TV Remote Event Listener with High-Frequency Rate Limiter
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      const isInputActive = activeTag === 'input' || activeTag === 'textarea';

      // Backspace handling in input
      if (isInputActive && (e.key === 'Backspace' || e.keyCode === 8)) {
        return;
      }

      // Allow arrow navigation inside inputs only if left/right
      if (isInputActive && ['ArrowLeft', 'ArrowRight'].includes(e.key)) {
        return;
      }

      const direction = isDirectionalEvent(e);
      if (direction) {
        e.preventDefault();
        
        // Throttling rapid D-pad repeat to prevent stuttering
        const now = Date.now();
        if (now - lastKeyActionTimeRef.current < 60) return; // 60ms minimum gap
        lastKeyActionTimeRef.current = now;

        navigateDirection(direction);
      } else if (isEnterEvent(e)) {
        if (!isInputActive) {
          e.preventDefault();
          triggerEnterAction();
        }
      } else if (isBackEvent(e)) {
        e.preventDefault();
        triggerBackAction();
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [enabled, navigateDirection, triggerEnterAction, triggerBackAction]);

  // 2. Fire TV Stick & Android TV Gamepad API Polling
  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || !navigator.getGamepads) return;

    let isPolling = true;
    const POLLING_INTERVAL_MS = 140; // Snappy 140ms repeat rate

    const pollGamepads = () => {
      if (!isPolling) return;

      try {
        const gamepads = navigator.getGamepads ? navigator.getGamepads() : [];
        const now = Date.now();

        for (let i = 0; i < gamepads.length; i++) {
          const gp = gamepads[i];
          if (!gp) continue;

          const isUp = (gp.buttons[12] && gp.buttons[12].pressed) || (gp.axes[1] && gp.axes[1] < -0.5);
          const isDown = (gp.buttons[13] && gp.buttons[13].pressed) || (gp.axes[1] && gp.axes[1] > 0.5);
          const isLeft = (gp.buttons[14] && gp.buttons[14].pressed) || (gp.axes[0] && gp.axes[0] < -0.5);
          const isRight = (gp.buttons[15] && gp.buttons[15].pressed) || (gp.axes[0] && gp.axes[0] > 0.5);
          const isSelect = (gp.buttons[0] && gp.buttons[0].pressed) || (gp.buttons[2] && gp.buttons[2].pressed);
          const isBack = (gp.buttons[1] && gp.buttons[1].pressed) || (gp.buttons[8] && gp.buttons[8].pressed);

          if (now - lastGamepadActionTimeRef.current > POLLING_INTERVAL_MS) {
            if (isUp) {
              lastGamepadActionTimeRef.current = now;
              navigateDirection('up');
            } else if (isDown) {
              lastGamepadActionTimeRef.current = now;
              navigateDirection('down');
            } else if (isLeft) {
              lastGamepadActionTimeRef.current = now;
              navigateDirection('left');
            } else if (isRight) {
              lastGamepadActionTimeRef.current = now;
              navigateDirection('right');
            } else if (isSelect) {
              lastGamepadActionTimeRef.current = now;
              triggerEnterAction();
            } else if (isBack) {
              lastGamepadActionTimeRef.current = now;
              triggerBackAction();
            }
          }
        }
      } catch (err) {
        // Benign gamepad access error
      }

      animationFrameRef.current = requestAnimationFrame(pollGamepads);
    };

    animationFrameRef.current = requestAnimationFrame(pollGamepads);

    return () => {
      isPolling = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [enabled, navigateDirection, triggerEnterAction, triggerBackAction]);

  return {
    focusElement,
    getFocusableElements,
    navigateDirection
  };
}

